import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import moment from 'moment';
import { MessageService } from '../../../../../services/message.service';
import { Paginator } from '../../../../../models/paginator';
import {
  CrawlUrl,
  CrawlerExecutionSummary,
  CrawlerUrlStatus,
  CrawlUrlDocumentRef,
} from '../../../../../models/crawlerUrl';
import { Option } from '../../../../../components/select/select.component';
import {
  CrawlUrlsService,
  CrawlUrlsFilters,
} from '../../../../../services/crawl-urls.service';

@Component({
  selector: 'app-crawl-urls-list',
  templateUrl: './crawl-urls-list.component.html',
  styleUrls: ['./crawl-urls-list.component.scss'],
})
export class CrawlUrlsListComponent implements OnInit {
  private integrationId?: string;
  loading: boolean = false;
  done: boolean = false;

  executions: CrawlerExecutionSummary[] = [];
  executionOptions: Option[] = [];

  paginator: Paginator<CrawlUrl> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: 'createdAt _id',
  };

  filters: CrawlUrlsFilters = {
    query: undefined,
    status: undefined,
    executionId: undefined,
  };

  readonly statusSelectOptions: Option[] = (['pending', 'success', 'failed'] as CrawlerUrlStatus[])
    .map((status) => ({
      label: `INTEGRATIONS.CRAWL_URLS.STATUSES.${status}`,
      value: status,
      selected: false,
      disabled: false,
    }));

  readonly displayedColumns: string[] = [
    'url',
    'status',
    'document',
    'error',
    'processedAt',
  ];

  private readonly filtersSubject = new Subject<CrawlUrlsFilters>();

  constructor(
    private readonly _message: MessageService,
    private readonly crawlUrlsService: CrawlUrlsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    firstValueFrom(this.route.params)
      .then(async (params) => {
        this.integrationId = params['id'];
        this.loading = true;
        this.done = false;
        await this.loadExecutions();
        await this.retrieveCrawlUrls();
        this.loading = false;
        this.done = true;
      });

    this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(async () => {
      this.resetPage();
      await this.retrieveCrawlUrls();
    });
  }

  private async loadExecutions() {
    if (!this.integrationId) return;
    try {
      this.executions = await this.crawlUrlsService.listExecutions(this.integrationId);
      // Default to the latest execution (list is sorted newest-first server-side).
      this.filters.executionId = this.executions[0]?._id;
      this.executionOptions = this.executions.map((execution) => ({
        label: this.executionLabel(execution),
        value: execution._id,
        selected: execution._id === this.filters.executionId,
        disabled: false,
      }));
    } catch (err) {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  private executionLabel(execution: CrawlerExecutionSummary): string {
    const date = execution.triggeredAt
      ? moment(execution.triggeredAt).format('YYYY-MM-DD HH:mm')
      : '';
    const status = execution.progress?.status ?? '';
    return `#${execution.executionIndex}${status ? ' · ' + status : ''}${date ? ' · ' + date : ''}`;
  }

  async retrieveCrawlUrls() {
    if (!this.integrationId) return;

    try {
      this._message.emitChange('LOADING', 'START');

      this.paginator = {
        ...this.paginator,
        ...await this.crawlUrlsService.retrieveCrawlUrlsPaginated(
          this.integrationId,
          this.paginator.page,
          this.paginator.limit,
          this.paginator.offset,
          this.paginator.sort,
          this.filters,
        ),
      };
    } catch (err) {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    } finally {
      this._message.emitChange('LOADING', 'END');
    }
  }

  resetPage() {
    this.paginator.limit = 10;
    this.paginator.page = 1;
    this.paginator.offset = 0;
  }

  async handlePage(event: PageEvent) {
    this.paginator.limit = event.pageSize;
    this.paginator.page = event.pageIndex + 1;
    this.paginator.offset = event.pageIndex * this.paginator.limit;
    await this.retrieveCrawlUrls();
  }

  async onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.paginator.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
    } else if (event.direction === '') {
      this.paginator.sort = 'createdAt';
    }
    await this.retrieveCrawlUrls();
  }

  onQueryChange() {
    this.filtersSubject.next({ ...this.filters });
  }

  onStatusChange(event?: { value: Option }) {
    this.filters = { ...this.filters, status: event?.value?.value };
    this.filtersSubject.next({ ...this.filters });
  }

  async onExecutionChange(event?: { value: Option }) {
    this.filters = { ...this.filters, executionId: event?.value?.value };
    this.resetPage();
    await this.retrieveCrawlUrls();
  }

  date(value?: string): string {
    return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
  }

  // documentId is populated to an object only when a document was produced.
  documentRef(crawlUrl: CrawlUrl): CrawlUrlDocumentRef | undefined {
    const doc = crawlUrl.documentId;
    return doc && typeof doc === 'object' ? doc : undefined;
  }

  openDocument(crawlUrl: CrawlUrl) {
    const doc = this.documentRef(crawlUrl);
    if (doc?._id) {
      this.router.navigate(['documents', doc._id]);
    }
  }
}
